import { useState, useEffect, useCallback, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { GuideArticle } from '../../data/guide-articles';
import { useUI } from '../../contexts/UIProvider';
import { useTheme } from '../../contexts';
import { useInlineAutoplay } from './use-inline-autoplay';
import { useChapterLoop } from './use-chapter-loop';
import { getGuideSlidesFromArticle, GuideSlide, SlideMediaItem } from './guide-slideshow-utils';
import GuideBlocks, { PhotoCompare, themedVideoSrc, videoMime } from './GuideBlocks';
import GuideShot from './GuideShot';

/** A horizontal drag at least this long, and mostly sideways, is a swipe. */
export const SWIPE_MIN_PX = 40;

/**
 * Which way a finished drag swipes: 1 to the next picture, -1 back, 0 for a
 * tap, a short drag or a mostly vertical one.
 */
export function swipeDirection(dx: number, dy: number): -1 | 0 | 1 {
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return 0;
  if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}

/** Autoplaying guide clip that works on iOS (forces the muted attribute + play). */
function SlideVideo({ src, start, end }: { src: string; start?: number; end?: number }) {
  const { theme } = useTheme();
  const themed = themedVideoSrc(src, theme);
  const mime = videoMime(themed);
  const ref = useRef<HTMLVideoElement>(null);
  useInlineAutoplay(ref, themed);
  useChapterLoop(ref, start, end, themed);
  return (
    <video ref={ref} key={themed} className="guide-clip guide-slideshow__video" autoPlay loop muted playsInline preload="auto">
      <source src={themed} type={mime} />
    </video>
  );
}

function MediaItem({ item }: { item: SlideMediaItem }) {
  switch (item.kind) {
    case 'video':
      return <SlideVideo src={item.block.src} start={item.block.start} end={item.block.end} />;
    case 'shot':
      return <GuideShot base={item.block.base} alt={item.block.alt} />;
    case 'compare':
      return <PhotoCompare pairs={[item.pair]} />;
    case 'cover':
      return <img src={item.src} alt={item.alt} className="guide-slideshow__cover" decoding="async" />;
  }
}

interface SlideMediaProps {
  item: SlideMediaItem;
  /** A sideways swipe on the picture moves to the next or previous slide. */
  onSwipe: (dir: -1 | 1) => void;
}

/**
 * The slide's picture: one per slide, on top, as large as the words below
 * leave room for. Swiping it changes slide; the words below are never a swipe
 * target, so a drag there selects text.
 */
function SlideMedia({ item, onSwipe }: SlideMediaProps) {
  const start = useRef<{ x: number; y: number } | null>(null);

  // Never preventDefault: a panning strip still scrolls by hand, and the page
  // keeps its own vertical scroll. A drag that starts on a panning strip is
  // the reader scrolling it, not a swipe.
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    start.current = target.closest('.guide-shot--pan') ? null : { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const from = start.current;
    start.current = null;
    if (!from) return;
    const dir = swipeDirection(e.clientX - from.x, e.clientY - from.y);
    if (dir !== 0) onSwipe(dir);
  };
  const onPointerCancel = () => {
    start.current = null;
  };

  return (
    <div
      className={`guide-slideshow__media guide-slideshow__media--${item.kind}`}
      data-testid="guide-slideshow-media"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <div className="guide-slideshow__stage">
        <MediaItem item={item} />
      </div>
    </div>
  );
}

interface GuideSlideshowPopupProps {
  guide: GuideArticle;
  initialSlideIndex?: number;
}

const GuideSlideshowPopup = ({ guide, initialSlideIndex = 0 }: GuideSlideshowPopupProps) => {
  const { setPopupOpen } = useUI();
  const slides = getGuideSlidesFromArticle(guide);
  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === slides.length - 1;

  const goNext = useCallback(() => {
    if (!isLast) setCurrentIndex((i) => i + 1);
  }, [isLast]);

  const goPrev = useCallback(() => {
    if (!isFirst) setCurrentIndex((i) => i - 1);
  }, [isFirst]);

  const swipe = useCallback((dir: -1 | 1) => (dir === 1 ? goNext() : goPrev()), [goNext, goPrev]);

  const close = useCallback(() => {
    setPopupOpen(false);
  }, [setPopupOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev]);

  if (slides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-[14px] text-gray-500 dark:text-neutral-400">No slides available for this guide yet.</p>
      </div>
    );
  }

  const slide: GuideSlide = slides[currentIndex];
  const articleUrl = `/guides/${guide.category}/${guide.slug}`;

  return (
    <div className="guide-slideshow flex flex-col h-full">
      {/* Header */}
      <div className="guide-slideshow__header flex items-center justify-between px-4 sm:px-5 py-5 border-b border-gray-300 dark:border-neutral-700">
        <h2 className="guide-slideshow__title text-[18px] font-semibold text-gray-900 dark:text-white truncate pr-4">{guide.title}</h2>
        <button
          onClick={close}
          className="text-gray-500 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white transition-colors shrink-0 w-8 h-8 flex items-center justify-center"
          aria-label="Close slideshow"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Picture on top, words below. Every slide has exactly one picture
          (guide-slideshow-utils); the words never carry one. */}
      <div className="guide-slideshow__body">
        <SlideMedia key={`media:${slide.key}`} item={slide.media} onSwipe={swipe} />

        {/* Keyed so each slide starts its words at the top, and so a numbered
            list picking up at step 3 is drawn fresh: Chrome does not redraw a
            list's numbers when only its `start` changes. */}
        <div key={`text:${slide.key}`} className="guide-slideshow__text">
          {slide.parent && <p className="guide-slideshow__eyebrow">{slide.parent}</p>}
          <h3 className="guide-slideshow__heading">{slide.heading}</h3>
          <GuideBlocks blocks={slide.text} />
        </div>
      </div>

      {/* Navigation — the dots and buttons stretch to fill the footer */}
      <div className="guide-slideshow__nav shrink-0 flex flex-col gap-3 px-4 sm:px-5 py-4 border-t border-gray-300 dark:border-neutral-700">
        <div
          className="flex items-center gap-2 w-full"
          role="tablist"
          aria-label="Slide indicators"
        >
          {slides.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setCurrentIndex(i)}
              className={`flex-1 h-2.5 rounded-full transition-colors ${
                i === currentIndex
                  ? 'bg-green-500'
                  : 'bg-gray-300 hover:bg-gray-400 dark:bg-neutral-600 dark:hover:bg-neutral-500'
              }`}
              role="tab"
              aria-selected={i === currentIndex}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* On a phone each button is as wide as its label plus 18px a side, and
            the spare width is shared out; equal thirds left "Full Article"
            touching its ends at 360px. */}
        <div className="flex items-stretch gap-2 sm:gap-3">
          <button
            onClick={goPrev}
            disabled={isFirst}
            className="flex-auto sm:flex-1 px-[18px] sm:px-4 py-3.5 rounded-full whitespace-nowrap text-[14px] sm:text-[15px] font-semibold bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-neutral-700 dark:text-white dark:hover:bg-neutral-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous slide"
          >
            Prev
          </button>

          {/* Opens the article the slideshow was built from, in its own tab so
              the slideshow stays where it is. */}
          <a
            href={articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-auto sm:flex-1 px-[18px] sm:px-4 py-3.5 rounded-full whitespace-nowrap text-[14px] sm:text-[15px] font-semibold text-center border border-gray-300 text-gray-900 hover:bg-gray-100 dark:border-neutral-600 dark:text-white dark:hover:bg-neutral-700 transition-colors"
          >
            Full Article
          </a>

          <button
            onClick={goNext}
            disabled={isLast}
            className="flex-auto sm:flex-1 px-[18px] sm:px-4 py-3.5 rounded-full whitespace-nowrap text-[14px] sm:text-[15px] font-semibold bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-neutral-700 dark:text-white dark:hover:bg-neutral-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Next slide"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideSlideshowPopup;
